import { z } from "zod";
import {
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import { IssueTable } from "../shared/issue-table";
import { useFavoritesList } from "./use-favorites-list";

/**
 * `favorites-list` — the viewer's starred projects, rendered through the shared
 * `IssueTable` (same live `DataTable` + `useIssueColumns` as `entity-list`). Unlike
 * `entity-list` it reads the starred set directly (`useFavoritesList`) and so bypasses
 * the dashboard scope fold — favorites aren't assignee-scoped, so a scoped read would
 * hide ones not assigned to the viewer. No Only-Me / advanced-filter header: those are
 * meaningless for an explicitly curated set.
 *
 * A **managed-only** card (the My Work catalog) — it contributes no `catalog` entry, so
 * it never appears in the free Add-card gallery.
 */
export const favoritesListConfigSchema = z
  .looseObject({
    sort: z.enum(["created", "updated", "name"]).default("updated"),
    home: z.boolean().default(false),
  });

export type FavoritesListConfig = z.infer<typeof favoritesListConfigSchema>;

function FavoritesListRender({ w, config }: WidgetRenderContext<FavoritesListConfig>) {
  const { rows, loading } = useFavoritesList(config.sort);
  return (
    <IssueTable
      rows={rows}
      loading={loading}
      variablesKey={config.sort}
      w={w}
      emptyIcon="star"
      emptyTitle="No favorites yet"
      emptyDescription="Star a project to see it here."
      groupLabel="Favorites"
      home={config.home}
    />
  );
}

const favoritesListSettings: WidgetSettingsGroup[] = [
  {
    title: "Data",
    fields: [
      {
        key: "sort",
        kind: "select",
        label: "Sort by",
        icon: "arrow-down-wide-narrow",
        options: [
          { value: "created", label: "Newest first" },
          { value: "updated", label: "Recently updated" },
          { value: "name", label: "Name (A–Z)" },
        ],
      },
    ],
  },
];

export const favoritesListDef: WidgetModule<FavoritesListConfig> = {
  type: "favorites-list",
  title: "Favorites",
  icon: "star",
  // A table has no meaningful chart view — its home card maximizes straight to
  // the data view (D13). The visually identical entity-list home card already
  // did this; the flag is what put the two on the same path.
  detailPresentation: "data",
  // Full-bleed body: the table's row dividers + selection wash reach the card
  // edge; the DataTable's own `paddingX` keeps cell content inset.
  bleed: true,
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 6, defaultH: 4 },
  configSchema: favoritesListConfigSchema,
  settings: favoritesListSettings,
  render: FavoritesListRender,
};
